import { HTMLAttributes, useEffect, useState } from 'react';
import { QuoteStatus, QuoteTagEnum, useLocalTranslation } from '@jove-frontend/uikit';
import clsx from 'clsx';
import { capitalize } from 'lodash';

export enum QuoteStatusViewTypeEnum {
  Quote = 'quote',
  Validation = 'validation',
}

type QuoteStatusViewProps = HTMLAttributes<HTMLDivElement> & {
  type?: QuoteStatusViewTypeEnum.Quote | QuoteStatusViewTypeEnum.Validation;
  label?: string;
  color?: string;
  status?: string;
  extraInfo?: {
    labels?: any;
  };
  size?: 'small' | 'medium' | 'large';
  info?: {
    labels?: any;
    promotionId?: string;
  };
};

const QuoteStatusView = (props: QuoteStatusViewProps) => {
  const { $t } = useLocalTranslation();
  const { className, type = QuoteStatusViewTypeEnum.Quote, label, color, status, extraInfo, size, info } = props;
  const [containerStyle, setContainerStyle] = useState({});
  const [showText, setShowText] = useState('');

  const quoteStatusTextMap: Record<string, string> = {
    [QuoteStatus.BROKER]: 'kit:broker',
    [QuoteStatus.FAIL]: 'kit:fail',
    [QuoteStatus.SUCCESS]: 'kit:success',
    [QuoteStatus.UNKNOWN]: 'kit:quotes.unknown',
    [QuoteStatus.QUOTE_START]: 'kit:quotes.quote_start',
    [QuoteStatus.DROPOUT_BEFORE_QUOTE]: 'kit:quotes.dropout_before_quote',
    [QuoteStatus.QUOTED_CONFIRMING]: 'kit:quotes.quoted_confirming',
    [QuoteStatus.QUOTED]: 'kit:quotes.quoted',
    [QuoteStatus.DROPOUT_BEFORE_SIGNING]: 'kit:quotes.dropout_before_signing',
    [QuoteStatus.PAYMENT_PENDING]: 'kit:quotes.payment_pending',
    [QuoteStatus.PAYMENT_PROCESSING]: 'kit:quotes.payment_processing',
    [QuoteStatus.DROPOUT_BEFORE_PURCHASE]: 'kit:quotes.dropout_before_purchase',
    [QuoteStatus.PURCHASED]: 'kit:quotes.purchased',
    [QuoteStatus.PURCHASED_NOT_SIGNED]: 'kit:quotes.purchased_not_signed',
    [QuoteStatus.PURCHASE_FAILED]: 'kit:quotes.purchase_failed',
    [QuoteStatus.SUBSCRIPTION_PAYMENT_FAILED]: 'kit:quotes.subscription_payment_failed',
    [QuoteStatus.DECLINED]: 'kit:quotes.declined',
    [QuoteStatus.NOT_INTERESTED]: 'kit:quotes.not_interested',
    [QuoteStatus.DUPLICATED]: 'kit:quotes.duplicated',
    [QuoteStatus.NOT_STARTED]: 'kit:quotes.not_started',
    [QuoteStatus.CANCELLED]: 'kit:quotes.cancelled',
    [QuoteStatus.IN_PROGRESS]: 'kit:quotes.in_progress',
    [QuoteStatus.POST_PURCHASE_LOGIN_CUSTOMER]: 'kit:quotes.post_purchase_login_customer',
    [QuoteStatus.POLICY_UPDATE_QUOTED]: 'kit:quotes.policy_update_quoted',
    [QuoteStatus.REFERRAL]: 'kit:quotes.referral',
    [QuoteStatus.PRE_PAYMENT]: 'kit:quotes.pre_payment',
    [QuoteStatus.OBTAINED]: 'kit:quotes.obtained',
    [QuoteStatus.PARTIALLY_OBTAINED]: 'kit:quotes.partially_obtained',
  };

  const getQuoteStatusText = (quoteStatus?: string) => {
    if (!quoteStatus) {
      return '';
    }

    const translationKey = quoteStatusTextMap[quoteStatus];
    if (translationKey) {
      return $t(translationKey);
    }

    return capitalize(quoteStatus.replaceAll('_', ' '));
  };

  /**
   * effects
   */
  useEffect(() => {
    evaluateTextInfo();
  }, [status, extraInfo, info]);

  /**
   * methods
   */
  const evaluateTextInfo = () => {
    let textColor = color;
    let showText = label || '';
    if (!textColor) {
      if (type === QuoteStatusViewTypeEnum.Quote) {
        switch (status) {
          case QuoteStatus.PURCHASE_FAILED:
          case QuoteStatus.SUBSCRIPTION_PAYMENT_FAILED:
          case QuoteStatus.CANCELLED:
          case QuoteStatus.BROKER:
          case QuoteStatus.DECLINED:
            textColor = '#DD1F1F';
            break;
          case QuoteStatus.NOT_INTERESTED:
          case QuoteStatus.NOT_STARTED:
          case QuoteStatus.DUPLICATED:
            textColor = '#D6D6D6';
            break;
          case QuoteStatus.QUOTED:
          case QuoteStatus.PURCHASED:
          case QuoteStatus.SUCCESS:
          case QuoteStatus.DIRECT_DEBIT_SETUP_SUCCESS:
            textColor = '#1CB258';
            break;
          case QuoteStatus.PAYMENT_PENDING:
          case QuoteStatus.PARTIALLY_OBTAINED:
          case QuoteStatus.DROPOUT_AFTER_SIGNING:
            textColor = '#43A7D2';
            break;
          case QuoteStatus.REFERRAL:
            textColor = '#E76F2B';
            break;
          default:
            textColor = '#0E35D1';
            break;
        }
      }
    }

    if (!label) {
      showText = getQuoteStatusText(status);
    }

    // Temporary logical judgment
    const labels = extraInfo?.labels || info?.labels || {};
    if (!!labels?.[QuoteTagEnum().partiallyQuoted.value]?.val && status === QuoteStatus.QUOTED) {
      showText = $t('kit:quoteTag.partiallyQuoted');
      textColor = '#1CB258';
    }
    if (!!info?.promotionId && status === QuoteStatus.BROKER) {
      showText = $t('kit:declined');
    }

    setShowText(showText);
    setContainerStyle({
      borderColor: `${textColor}`,
      color: `${textColor}`,
    });
  };

  const getButtonContainerSize = () => {
    if (size === 'small') {
      return 'rounded px-[2px] py-[1px] text-xs font-medium';
    } else if (size === 'medium') {
      return 'rounded px-[5px] py-[3px] text-[13px] font-medium';
    } else {
      return 'rounded-md px-[8px] py-[6px] text-sm font-bold';
    }
  };

  return (
    <div style={containerStyle} className={clsx('flex flex-row border', className, getButtonContainerSize())}>
      {showText}
    </div>
  );
};

export default QuoteStatusView;
